import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { Box, Button, Link, Text } from '@factorialco/deprecated-design-system'
import { InvoiceReconciliationDocument } from 'generated/resources/graphql'
import { sum } from 'lodash'

import Modal from 'modules/core/components/Modal'
import Sidepanel from 'modules/core/components/Sidepanel'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import { ReconciliationInvoiceSuggestions } from 'modules/banking/components/NewInvoiceReconciliationForm/ReconciliationInvoiceSuggestions'
import { ReconciliationsTransactionsDataTable } from 'modules/banking/components/NewInvoiceReconciliationForm/ReconciliationsTransactionsDataTable'
import { ReconciliationSummary } from 'modules/banking/components/NewReconciliationForm/ReconciliationSummary'
import { SplitReconciledAmountsModal } from 'modules/banking/components/NewReconciliationForm/SplitReconciledAmountsModal'
import { TagLabelComponent } from 'modules/banking/components/NewReconciliationForm/TagLabelComponent'
import {
  ReconciliationItem,
  ReconciliationSource,
  ReconciliationStatusEnum,
  ReconciliationType,
  useReconciliationForm,
} from 'modules/banking/hooks/useReconciliationForm'
import { invoiceType } from 'modules/banking/lib/invoicesHelpers'
import { formattedAmount } from 'modules/banking/lib/paymentHelpers'

const NewReconciliationPageFromInvoice = () => {
  const { invoiceId: invoiceId } = useParams()
  const { company } = useCurrentSession()
  const { navigation } = useNavigation()
  const navigate = useNavigate()

  const { data, loading } = useQuery(InvoiceReconciliationDocument, {
    variables: { companyId: company.id, financialDocumentId: Number(invoiceId) },
    skip: !invoiceId,
  })

  const invoice = (invoiceId && data?.finance?.financialDocumentsConnection?.nodes?.[0]) || null

  const { access } = useCurrentSession()
  const locale = access.locale

  const [showSplitAmountsModal, setShowSplitAmountsModal] = useState<boolean>(false)
  const [showPartialPendingInvoiceModal, setShowPartialPendingInvoiceModal] =
    useState<boolean>(false)
  const [showPartialPendingTransactionModal, setShowPartialPendingTransactionModal] =
    useState<boolean>(false)
  const [source, setSource] = useState<ReconciliationSource | undefined>(undefined)
  const [sourceReconciliations, setSourceReconciliations] = useState<ReconciliationItem[]>([])
  const [showSuggestions, setShowSuggestions] = useState<boolean>(true)

  useUnsafeEffect(() => {
    if (!invoice) {
      setSource(undefined)
      setSourceReconciliations([])
    } else {
      const reconciliationStatus = invoice.fullyReconciledAt
        ? ReconciliationStatusEnum.Full
        : invoice.reconciliations.length
        ? ReconciliationStatusEnum.Partial
        : ReconciliationStatusEnum.None

      setSource({
        id: invoice.id,
        description: invoice.documentNumber || '',
        amountCentsToReconcile: invoice.totalAmountCents && Math.abs(invoice.totalAmountCents),
        secondaryDescription: invoice.vendor?.legalName || '',
        currency: invoice.currency || '',
        date: invoice.documentDate || '',
        locale: locale,
        type:
          invoice.totalAmountCents &&
          invoiceType(
            true, // We are assuming all invoices are expenses invoices and not income invoices
            invoice.totalAmountCents
          ),
        reconciliationStatus: reconciliationStatus,
        reconciliationType: ReconciliationType.Invoice,
      })

      setSourceReconciliations(
        (invoice.reconciliations || [])
          .slice()
          .sort((a, b) => {
            return a.id - b.id
          })
          .map((r) => {
            return {
              reconciliationId: r.id,
              id: r.transactionId,
              description: r.transaction.description || '-',
              secondaryDescription: '',
              amountCentsToReconcile: Math.abs(r.reconcilableReconciledAmountCents || 0),
              currency: r.transaction.currency || '',
              date: r.transaction.bookingDate || '',
              locale: locale,
              type: r.transaction.amountCents > 0 ? 'debit' : 'credit',
            }
          })
      )
    }
  }, [invoice, invoice?.reconciliations, locale])

  const {
    handleReconcileClick,
    handleSubmitSplitAmountsModal,
    handleSubmitPartialModal,
    removeReconciliation,
    addReconciliation,
    isSelectedNewReconciliation,
    dirtyForm,
    selectedReconciliations,
    newReconciliations,
    initialAmountPendingToBeReconciled,
    firstReconciliationAmountCents,
    firstReconciliationAmountCentsToReconcile,
  } = useReconciliationForm({
    source,
    sourceReconciliations,
    setShowSplitAmountsModal,
    setShowPartialPendingInvoiceModal,
    setShowPartialPendingTransactionModal,
  })

  const canShowSuggestions =
    !!invoice &&
    invoice.reconciliationSuggestions.length > 0 &&
    !invoice.fullyReconciledAt &&
    !loading

  const footerModalButtons = (
    <>
      <Button
        type="secondary"
        onClick={() => {
          setShowPartialPendingInvoiceModal(false)
          setShowPartialPendingTransactionModal(false)
          setShowSplitAmountsModal(false)
        }}
      >
        {i18n.t('banking.reconciliations.common_modal.cancel')}
      </Button>
      <Button onClick={() => handleSubmitPartialModal()}>
        {i18n.t('banking.reconciliations.common_modal.confirm')}
      </Button>
    </>
  )
  return (
    <Sidepanel
      title={i18n.t('banking.accounts.reconciliations')}
      width="extended"
      open
      onClose={() =>
        // eslint-disable-next-line no-type-assertion/no-type-assertion
        navigate(navigation.finance.invoices.list.invoiceId.fullPath({ invoiceId: invoiceId! }))
      }
      footer={
        <TagLabelComponent dirty={dirtyForm} source={source} onReconcile={handleReconcileClick} />
      }
    >
      <Box>
        <Box flexDirection="column" gap="s16">
          <Box flexDirection="column" gap="s4">
            <Text.Normal weight="semibold">{i18n.t('banking.reconciliations.title')}</Text.Normal>
            <Text.Normal weight="normal">
              {i18n.t('banking.reconciliations.description')}
            </Text.Normal>
          </Box>
          <Box>
            <ReconciliationSummary
              source={source}
              reconciliations={selectedReconciliations}
              onRemove={removeReconciliation}
              loading={loading}
              locale={locale}
            />
          </Box>

          {canShowSuggestions && showSuggestions && (
            <ReconciliationInvoiceSuggestions
              invoice={invoice}
              onHideSuggestions={() => setShowSuggestions(false)}
            />
          )}

          {(!canShowSuggestions || !showSuggestions) && (
            <>
              <Box
                flexDirection="row"
                width="full"
                alignItems="center"
                justifyContent="spaceBetween"
              >
                <Text.Normal weight="semibold">
                  {i18n.t('banking.reconciliations.transactions')}
                </Text.Normal>
                {canShowSuggestions && (
                  <Link.Touchable onClick={() => setShowSuggestions(true)}>
                    {i18n.t('banking.reconciliations.suggestions.show')}
                  </Link.Touchable>
                )}
              </Box>
              <Box
                style={{
                  marginTop: '8px',
                  minHeight: '240px',
                  maxHeight: 'calc(100vh - 534px)',
                }}
                overflowY="auto"
              >
                <ReconciliationsTransactionsDataTable
                  onSelect={addReconciliation}
                  onRemove={removeReconciliation}
                  isSelected={isSelectedNewReconciliation}
                  currency={invoice?.currency}
                  disabled={!source}
                />
              </Box>
            </>
          )}
        </Box>
        {source && showPartialPendingTransactionModal && (
          <Modal
            onClose={() => setShowPartialPendingTransactionModal(false)}
            title={i18n.t('banking.reconciliations.partial_invoice_modal.title')}
            description={i18n.t('banking.reconciliations.partial_invoice_modal.subtitle', {
              partial: formattedAmount({
                amountCents: sum(selectedReconciliations.map((el) => el.amountCentsToReconcile)),
                currency: source.currency,
                locale: locale,
              }),
              total: formattedAmount({
                amountCents: source.amountCentsToReconcile,
                currency: source.currency,
                locale: locale,
              }),
            })}
            illustration="alert"
            footer={footerModalButtons}
          />
        )}
        {source &&
          showPartialPendingInvoiceModal &&
          firstReconciliationAmountCents &&
          firstReconciliationAmountCentsToReconcile && (
            <Modal
              onClose={() => setShowPartialPendingInvoiceModal(false)}
              title={i18n.t('banking.reconciliations.partial_transaction_modal.title')}
              description={i18n.t('banking.reconciliations.partial_transaction_modal.subtitle', {
                partial: formattedAmount({
                  amountCents: firstReconciliationAmountCents,
                  currency: source.currency,
                  locale: locale,
                }),
                total: formattedAmount({
                  amountCents: firstReconciliationAmountCentsToReconcile,
                  currency: source.currency,
                  locale: locale,
                }),
              })}
              illustration="alert"
              footer={footerModalButtons}
            />
          )}
        {source && showSplitAmountsModal && (
          <SplitReconciledAmountsModal
            type={ReconciliationType.Invoice}
            onClose={() => setShowSplitAmountsModal(false)}
            onSubmit={(items) => handleSubmitSplitAmountsModal(items)}
            selectedReconcilables={newReconciliations.map((reconciliation) => ({
              ...reconciliation,
              originalAmountCents: reconciliation.amountCentsToReconcile,
              updatedAmountCents: reconciliation.amountCentsToReconcile,
            }))}
            totalAmountCents={initialAmountPendingToBeReconciled}
            totalAmountCurrency={source.currency}
            locale={source.locale}
          />
        )}
      </Box>
    </Sidepanel>
  )
}

export default NewReconciliationPageFromInvoice
